import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useWelcomeKitPageStore } from 'store/pages/welcome-kit-page/pageStore';
import {
  StapleTable,
  StapleTableHeader,
  StapleTableHeading,
  StapleTableRow,
  StapleTableCell,
  Button,
  Tooltip,
  ActionModal,
} from '@insurancemenu/imenu-components-lib';
import { File } from 'types/policyService';
import { Download } from '@insurancemenu/imenu-components-lib/dist/icons';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import {
  DateTableHeadingStyled,
  FileIndexStyled,
  FilesCountStyled,
  TableInfoSectionStyled,
  TotalFilesTextStyled,
  DownloadButtonWrapper,
  StapleTableBodyStyled,
  ActionBarStyled,
} from './styles';
import MoreDropdown from '../MoreDropdown';

const TableRow: React.FC<{ file: File; fileIndex: number }> = observer(({ file, fileIndex }) => {
  function onDownload(): void {
    window.open(`https://${process.env.REACT_APP_API_BASE_URL}/docxBinary/Prod/downloadPdf/${file.id}`, '_blank');
  }
  return (
    <StapleTableRow>
      <StapleTableCell width="90%">
        <FileIndexStyled>{fileIndex + 1}. </FileIndexStyled>
        {file.name}
      </StapleTableCell>
      <StapleTableCell width="10%" onClick={onDownload}>
        <DownloadButtonWrapper>
          <Tooltip label="Download File" alignment={AlignmentTypes.LEFT_CENTER}>
            <Button type="icon" color="primary">
              <Download />
            </Button>
          </Tooltip>
        </DownloadButtonWrapper>
      </StapleTableCell>
    </StapleTableRow>
  );
});

const FilesTable: React.FC = observer(() => {
  const {
    files,
    isLoading,
    welcomeLetterModalDelegate,
    confirmationLetterModalDelegate,
    getInitialData,
    triggerAlliedWelcomeLetter,
    triggerAlliedConfirmationLetter,
  } = useWelcomeKitPageStore();

  useEffect(() => {
    void getInitialData();
  }, []);

  const creationDate = files[0]?.creationDate ? normDateToShortAmericanDate(files[0]?.creationDate) : '';

  return (
    <div>
      <ActionBarStyled>
        <MoreDropdown />
      </ActionBarStyled>
      <TableInfoSectionStyled>
        <TotalFilesTextStyled>
          Total Files:<FilesCountStyled> {files.length} </FilesCountStyled>
        </TotalFilesTextStyled>
      </TableInfoSectionStyled>
      <StapleTable totalElements={files.length} loading={isLoading}>
        <StapleTableHeader>
          <StapleTableRow>
            <StapleTableHeading width="80%">File Name</StapleTableHeading>
            <DateTableHeadingStyled width="4rem">{creationDate && `Date: ${creationDate}`}</DateTableHeadingStyled>
          </StapleTableRow>
        </StapleTableHeader>
        <StapleTableBodyStyled>
          {files.map((file, index) => (
            <TableRow key={file.id} file={file} fileIndex={index} />
          ))}
        </StapleTableBodyStyled>
      </StapleTable>
      <ActionModal
        title="Trigger Welcome Letter"
        subtitle="Please confirm to proceed"
        show={welcomeLetterModalDelegate.isVisible}
        loading={welcomeLetterModalDelegate.isLoading}
        onAction={triggerAlliedWelcomeLetter}
        onClose={welcomeLetterModalDelegate.close}
        modalType={ModalType.EMAIL}
        actionButtonText="Confirm"
        closeButtonText="Cancel"
      />
      <ActionModal
        title="Trigger Confirmation Letter"
        subtitle="Please confirm to proceed"
        show={confirmationLetterModalDelegate.isVisible}
        loading={confirmationLetterModalDelegate.isLoading}
        onAction={triggerAlliedConfirmationLetter}
        onClose={confirmationLetterModalDelegate.close}
        modalType={ModalType.EMAIL}
        actionButtonText="Confirm"
        closeButtonText="Cancel"
      />
    </div>
  );
});

export default FilesTable;
